import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { firstValueFrom } from 'rxjs';

/**
 * ✅ SIMPLIFIED UserService
 * 
 * Removed:
 *   - getDeviceId()
 *   - saveDeviceId()
 *   - getUserId()
 *   - getUserIdSync()
 *   - deviceIdSubject
 *   - deviceId$
 *   - DEVICE_ID_KEY
 * 
 * Kept:
 *   - getCurrentUser() - reads current_user from IDB
 *   - saveUser() - writes current_user to IDB
 *   - generateRandomUsername() - generates random usernames
 * 
 * Purpose: Minimal IDB storage manager for user data
 */
@Injectable({
  providedIn: 'root'
})


export class UserService {

  private db: IDBDatabase | null = null;
  private readonly DB_NAME = 'user_database';
  private readonly STORE_NAME = 'user_store';
  private readonly DB_VERSION = 1;

  constructor() {
    this.initDatabase().subscribe();
  }

  /**
   * Initialize IndexedDB connection
   */
  initDatabase(): Observable<string> {
    return from(new Promise<string>((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = (event) => {
        console.error('Error opening database:', event);
        reject('Failed to open database');
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        console.log('✅ IndexedDB initialized');
        resolve('Database ready');
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME, { keyPath: 'key' });
          console.log('✅ Created object store:', this.STORE_NAME);
        }
      };
    }));
  }

  /**
   * Get current user from IDB
   * 
   * Returns: {
   *   id: "SapphireArmadillo62",
   *   displayName: "SapphireArmadillo62",
      *   lat: 40.741651,
   *   lon: -73.960847
   * }
   */
  async getCurrentUser(): Promise<{
    id: string;
    displayName: string;
    lat: number | null;
    lon: number | null;
  } | null> {
    if (!this.db) {
      await firstValueFrom(this.initDatabase());
    }

    try {
      return await new Promise<{
        id: string;
        displayName: string;
        lat: number | null;
        lon: number | null;
      } | null>((resolve, reject) => {
        const transaction = this.db!.transaction(this.STORE_NAME, 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.get('current_user');

        request.onerror = () => reject('Failed to get user');
        request.onsuccess = () => {
          const rawResult = (request as IDBRequest<any>).result;
          resolve(rawResult?.value || null);
        };
      });
    } catch (error) {
      console.warn('[getCurrentUser] IDB read failed, trying localStorage fallback:', error);

      // Fallback to localStorage
      const stored = localStorage.getItem('current_user');
      try {
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    }
  }

  async getDisplayNameFromIDB(): Promise<string> {
    //console.log('[PhotoDetailModal] Attempting to get device ID from IndexedDB');
    return new Promise((resolve, reject) => {
      //console.log('[PhotoDetailModal] Opening user_database');
      const request = indexedDB.open('user_database', 1);

      request.onupgradeneeded = (event) => {
        //  console.log('[PhotoDetailModal] Database upgrade needed event triggered');
      };

      request.onerror = (event) => {
        console.error('[PhotoDetailModal] Error opening IndexedDB:', event);
        console.error('[PhotoDetailModal] Error details:', (event.target as any).error);
        reject('Error opening database');
      };

      request.onsuccess = (event) => {
        // console.log('[PhotoDetailModal] Database opened successfully');
        const db = request.result;

        // Log available object stores
        // console.log('[PhotoDetailModal] Available object stores:', Array.from(db.objectStoreNames));

        try {
          // console.log('[PhotoDetailModal] Creating transaction for user_store');
          const transaction = db.transaction(['user_store'], 'readonly');

          transaction.onerror = (event) => {
            console.error('[PhotoDetailModal] Transaction error:', event);
            reject('Transaction error');
          };

          //console.log('[PhotoDetailModal] Getting user_store object store');
          const store = transaction.objectStore('user_store');

          // console.log('[PhotoDetailModal] Requesting device_id from store');
          // Get the 'device_id' record which contains a key-value pair
          const getRequest = store.get('device_id');

          getRequest.onerror = (event) => {
            console.error('[PhotoDetailModal] Error reading from IndexedDB:', event);
            reject('Error reading from database');
          };

          getRequest.onsuccess = (event) => {
            // console.log('[PhotoDetailModal] Get request completed successfully');
            const result = getRequest.result;
            // console.log('[PhotoDetailModal] Raw result:', result);

            // The IDB format is {key: 'device_id', value: 'ObnoxiousSheep5'}
            if (result && result.value) {
              // console.log('[PhotoDetailModal] Device ID from IndexedDB:', result.value);
              resolve(result.value);
            } else {
              console.warn('[PhotoDetailModal] Device ID not found in IndexedDB');
              resolve('');
            }
          };
        } catch (error) {
          console.error('[PhotoDetailModal] Error in IndexedDB operations:', error);
          reject(`Error in IndexedDB operations: ${error}`);
        }
      };
    });
  }

  /**
   * Save current user to IDB
   * 
   * Accepts: {
   *   id: "SapphireArmadillo62",
   *   displayName: "SapphireArmadillo62",
      *   lat: 40.741651,
   *   lon: -73.960847
   * }
   */
  async saveUser(user: {
    id: string;
    displayName: string;
    lat: number | null;
    lon: number | null;
  }): Promise<void> {
    if (!this.db) {
      await firstValueFrom(this.initDatabase());
    }

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db!.transaction(this.STORE_NAME, 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.put({ key: 'current_user', value: user });

      request.onerror = () => {
        console.error('IDB save failed:', request.error);
        reject('Failed to save user');
      };

      request.onsuccess = () => {
        console.log('✅ User saved to IDB:', user.id);
        resolve();
      };
    }).catch((error) => {
      console.warn('[saveUser] IDB write failed, falling back to localStorage:', error);
      // Fallback to localStorage
      localStorage.setItem('current_user', JSON.stringify(user));
    });
  }

  /**
   * Generate random username (adjective + animal + number)
   * e.g., "MysteriousFox123", "SapphireArmadillo62"
   */
  generateRandomUsername(): string {
    const adjectives = [
      'Mysterious', 'Silent', 'Swift', 'Brave', 'Clever', 'Gentle',
      'Bright', 'Calm', 'Bold', 'Wise', 'Quick', 'Quiet', 'Sharp',
      'Cosmic', 'Digital', 'Cyber', 'Phantom', 'Shadow', 'Neon',
      'Fierce', 'Vivid', 'Crimson', 'Lunar', 'Solar', 'Radiant',
      'Stormy', 'Frozen', 'Velvet', 'Scarlet', 'Iron', 'Golden',
      'Emerald', 'Sapphire', 'Ruby', 'Ivory', 'Obsidian', 'Steel',
      'Ancient', 'Epic', 'Majestic', 'Wild', 'Arcane', 'Electric',
      'Nocturnal', 'Blazing', 'Glacial', 'Savage', 'Ethereal', 'Serene'
    ];

    const animals = [
      'Fox', 'Wolf', 'Eagle', 'Tiger', 'Bear', 'Lion', 'Owl',
      'Hawk', 'Raven', 'Dolphin', 'Shark', 'Falcon', 'Leopard',
      'Panda', 'Koala', 'Penguin', 'Octopus', 'Butterfly', 'Firefly',
      'Cheetah', 'Panther', 'Moose', 'Bison', 'Crocodile', 'Seal',
      'Whale', 'Parrot', 'Hummingbird', 'Toucan', 'Chameleon',
      'Giraffe', 'Zebra', 'Kangaroo', 'Sloth', 'Armadillo',
      'Lynx', 'Walrus', 'Manatee', 'Tortoise', 'Jellyfish',
      'Badger', 'Ocelot', 'Antelope', 'Gorilla', 'Jaguar',
      'Porcupine', 'Hedgehog', 'Weasel', 'Pelican', 'Heron',
      'Vulture', 'Cormorant', 'Orangutan', 'Gazelle', 'Beetle',
      'Lobster', 'Barracuda', 'Stingray', 'Mongoose', 'Ibex'
    ];

    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const animal = animals[Math.floor(Math.random() * animals.length)];
    const number = Math.floor(Math.random() * 999) + 1;

    return `${adjective}${animal}${number}`;
  }

  /**
   * Clear all IDB data (for testing/reset)
   */
  async clearAllData(): Promise<void> {
    if (!this.db) {
      await firstValueFrom(this.initDatabase());
    }

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db!.transaction(this.STORE_NAME, 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.clear();

      request.onerror = () => reject('Failed to clear data');
      request.onsuccess = () => {
        console.log('✅ IDB cleared');
        resolve();
      };
    });
  }
}